<template>
    <DialogLayout :class-name="'add-candidate-dialog'" @close="handleClose">
        <template #title>{{ t('employee.addEmployee') }}</template>
        <template #default>
            <div class="dialog-content">
                <div class="method-row">
                    <div class="method-label">{{ t('employee.addCandidateDialog.addMethod') }}</div>
                    <el-radio-group v-model="selectedMethod" class="method-options">
                        <el-radio :value="'direct'">{{ t('employee.addCandidateDialog.directAdd') }}</el-radio>
                        <el-radio :value="'ai'">{{ t('employee.addCandidateDialog.aiParseResume') }}</el-radio>
                    </el-radio-group>
                </div>

                <div v-if="selectedMethod === 'ai'" class="upload-section">
                    <FileUpload
                        :title="t('employee.addCandidateDialog.uploadResume')"
                        :max-count="1"
                        :max-size="maxFileSizeMb"
                        :tips="t('employee.form.documents.other.tips')"
                        accept=".webp,.jpg,.jpeg,.png,.doc,.docx,.xlsx,.xls,.pdf"
                        :file-list="uploadedFile"
                        :disabled="false"
                        :show-preview="false"
                        @update:file-list="handleSkillCertUpdate"
                        @upload="handleSkillCertUpload"
                        @delete="handleSkillCertDelete"
                    />
                </div>
            </div>
        </template>
        <template #footer>
            <el-button class="confirm-button-default" @click="handleCancel">
                {{ t('common.cancel') }}
            </el-button>
            <el-button
                type="primary"
                :loading="submitting"
                :disabled="isConfirmDisabled"
                :class="{ 'is-disabled-opacity': isConfirmDisabled }"
                @click="handleConfirm"
            >
                {{ confirmButtonText }}
            </el-button>
        </template>
    </DialogLayout>
</template>

<script setup lang="ts">
import { ref, computed, watch, h } from 'vue'
import { ElMessage } from 'element-plus'
import DialogLayout from '@/components/business-components/dialog/dialog-layout.vue'
import { uploadAttachmentApi, resumeAnalysisApi, type AttachmentInfo } from '@/api/candidate'
import { useI18n } from 'vue-i18n'
import FileUpload from '@/components/business-components/file-upload/index.vue'
import type { AddCandidateDialogProps, AddCandidateDialogResult, AddCandidateMethod } from './add-candidate-dialog-types'
import loadingGif from '@/assets/icons/loading.gif';

interface UploadedFile {
  uid?: string
  name?: string
  fileName?: string
  url?: string
  fileKey?: string
}

const { t } = useI18n()
const props = withDefaults(defineProps<AddCandidateDialogProps>(), {
    defaultMethod: 'direct',
    maxFileSizeMb: 10
})

const emit = defineEmits<{
  cancel: []
  confirm: [data: AddCandidateDialogResult]
  close: []
}>()

const selectedMethod = ref<AddCandidateMethod>(props.defaultMethod)
const uploading = ref(false)
const submitting = ref(false)
const uploadedFile = ref<UploadedFile[]>([])
const uploadedAttachment = ref<AttachmentInfo | null>(null)
const loadingModal = ref<ReturnType<typeof ElMessage> | null>(null)

const maxFileSizeMb = computed(() => props.maxFileSizeMb)

const confirmButtonText = computed(() => {
  return selectedMethod.value === 'ai'
    ? t('employee.addCandidateDialog.startParse')
    : t('common.addBtn')
})

const isConfirmDisabled = computed(() => {
  if (selectedMethod.value !== 'ai') {
    return submitting.value
  }
  return uploadedFile.value.length === 0 || uploading.value || submitting.value
})

watch(selectedMethod, (value) => {
  if (value === 'direct') {
    uploadedFile.value = []
    uploadedAttachment.value = null
  }
})

const handleSkillCertUpdate = (fileList: UploadedFile[]) => {
  uploadedFile.value = fileList
}

const handleSkillCertUpload = async (file: File) => {
  try {
    uploading.value = true
    const result = await uploadAttachmentApi(file)
    uploadedAttachment.value = result
    uploadedFile.value = [{
      fileName: result.fileName,
      url: result.url,
      fileKey: result.s3Key
    }]
    ElMessage.success(t('common.uploadSuccess'))
  } catch (error) {
    console.error('上传简历失败:', error)
    ElMessage.error((error as Error).message || t('common.uploadFailed'))
  } finally {
    uploading.value = false
  }
}

const handleSkillCertDelete = () => {
  uploadedFile.value = []
  uploadedAttachment.value = null
}

const handleCancel = () => {
  submitting.value = false
  if (loadingModal.value) {
    loadingModal.value.close()
    loadingModal.value = null
  }
  emit('cancel')
}

const handleConfirm = async () => {
  if (selectedMethod.value === 'ai' && uploadedFile.value.length === 0) {
    ElMessage.error(t('employee.addCandidateDialog.uploadRequired'))
    return
  }

  if (selectedMethod.value === 'direct') {
    emit('confirm', {
      method: selectedMethod.value,
    })
    return
  }

  submitting.value = true
  loadingModal.value = ElMessage({
    message: t('common.parseLoading'),
    duration: 0,
    showClose: false,
    icon: h('img', { src: loadingGif, style: 'width: 20px; height: 20px;' })
  });
  try {
    const s3Key = uploadedAttachment.value?.s3Key
    if (!s3Key) {
      ElMessage.error(t('employee.addCandidateDialog.uploadRequired'))
      return
    }
    const data = await resumeAnalysisApi(s3Key)
    const parsed = (data && typeof data === 'object' && 'data' in data)
      ? (data as { data?: Record<string, unknown> }).data
      : data
    ElMessage.success(t('common.parseSuccess'))
    emit('confirm', {
      method: selectedMethod.value,
      data: (parsed || undefined) as Record<string, unknown> | undefined
    })
  } catch (error) {
    console.error('AI 解析简历失败:', error)
    ElMessage.error(t('common.parseFailed'))
  } finally {
    if (loadingModal.value) {
      loadingModal.value.close()
      loadingModal.value = null
    }
    submitting.value = false
  }
}

const handleClose = () => {
  if (!submitting.value) {
    emit('close')
  }
}
</script>

<style scoped lang="scss">
.add-candidate-dialog {
  min-width: 520px;
  background: #ffffff;
}

.dialog-content {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.method-row {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.method-label {
  color: #495057;
  font-size: 14px;
  font-weight: normal;
}

.method-options {
  display: flex;
  gap: 20px;
}

.upload-section {
  background: #F7F8FA;
  border-radius: 8px;
  padding: 12px 8px;
  gap: 8px;
  display: flex;
  flex-direction: column;
  width: 460px;
}

.is-disabled-opacity {
    background-color: #8755DE !important;
    opacity: 0.5;
}

</style>
